import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  FiSearch,
  FiPhone,
  FiX,
  FiPaperclip,
  FiImage,
  FiFile,
  FiArrowLeft,
  FiVideo,
  FiMic,
  FiSend,
  FiMessageSquare,
} from "react-icons/fi";
import { BsCheck2, BsCheck2All } from "react-icons/bs";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import {
  getChatUsers,
  getConversations,
  getMessages,
  sendMessage,
  subscribeToChat,
  uploadChatFile,
  markConversationRead,
  resolveFileUrl,
} from "../../services/chat_service";
import { getCurrentUser } from "../../services/auth_service";
import "./Chat.css";

const isImageFile = (name = "") => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
const toDate = (iso) => {
  if (!iso) return null;
  const normalized = /Z|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatTime = (iso) => {
  const d = toDate(iso);
  return d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
};

const formatDayLabel = (iso) => {
  const d = toDate(iso);
  if (!d) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function MessageTicks({ read }) {
  if (read) {
    return (
      <span className="chat-ticks chat-ticks--read">
        <BsCheck2All size={14} />
      </span>
    );
  }
  return (
    <span className="chat-ticks">
      <BsCheck2 size={14} />
    </span>
  );
}

export default function Chat() {
  const currentUser = getCurrentUser();
  const myId = Number(currentUser.user_id);
  const location = useLocation();
  const [allUsers, setAllUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);
  const activeUserRef = useRef(null);
  const [mobileView, setMobileView] = useState("list");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [messageSearchOpen, setMessageSearchOpen] = useState(false);
  const [messageSearch, setMessageSearch] = useState("");
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [audioSeconds, setAudioSeconds] = useState(0);
  const audioRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioTimerRef = useRef(null);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [videoPreviewStream, setVideoPreviewStream] = useState(null);
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const videoPreviewRef = useRef(null);

  useEffect(() => {
    activeUserRef.current = activeUser;
  }, [activeUser]);

  useEffect(() => {
    getChatUsers().then(setAllUsers).catch(console.error);
    getConversations().then(setConversations).catch(console.error);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToChat((incoming) => {
      const active = activeUserRef.current;
      const activeId = active?.id ?? active?.user_id;
      if (incoming.type === "read_receipt") {
        setMessages((prev) =>
          prev.map((m) =>
            incoming.message_ids?.includes(m.id) ? { ...m, is_read: true } : m
          )
        );
        return;
      }
      if (
        active &&
        (Number(incoming.sender_id) === Number(activeId) ||
          Number(incoming.receiver_id) === Number(activeId))
      ) {
        setMessages((prev) =>
          prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]
        );
        if (Number(incoming.sender_id) === Number(activeId)) {
          markConversationRead(activeId).catch(() => { });
        }
      }
      getConversations().then(setConversations).catch(console.error);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  useEffect(() => {
    if (!attachMenuOpen) return;
    const handler = (e) => {
      if (!e.target.closest(".chat-attach-wrap")) setAttachMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [attachMenuOpen]);
  useEffect(() => {
    if (!lightboxImage) return;
    const handler = (e) => {
      if (e.key === "Escape") setLightboxImage(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [lightboxImage]);

  const openConversation = async (user) => {
    setActiveUser(user);
    setMessageSearchOpen(false);
    setMessageSearch("");
    setMobileView("chat");
    const userId = user.id ?? user.user_id;
    const msgs = await getMessages(userId);
    setMessages(msgs);
    try {
      await markConversationRead(userId);
      getConversations().then(setConversations).catch(console.error);
    } catch (err) {
      console.error("Failed to mark conversation read", err);
    }
  };

  const backToList = () => {
    setMobileView("list");
  };

  // Auto-open a conversation when arriving from a notification click
  // (Header navigates here with { state: { openUserId } }).
  useEffect(() => {
    const targetId = location.state?.openUserId;
    if (!targetId || allUsers.length === 0) return;

    const match = allUsers.find(
      (u) => Number(u.id ?? u.user_id) === Number(targetId)
    );
    if (match) {
      openConversation(match);
      // Clear the navigation state so refreshing or navigating back
      // doesn't keep re-triggering this.
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, allUsers]);

  const handleSend = async () => {
    if (!draft.trim() || !activeUser) return;
    const targetId = activeUser.id ?? activeUser.user_id;
    const sent = await sendMessage(targetId, draft.trim());
    setMessages((prev) =>
      prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]
    );
    setDraft("");
  };

  const handleFileSelected = async (file, messageType) => {
    if (!file || !activeUser) return;
    const targetId = activeUser.id ?? activeUser.user_id;
    setUploading(true);
    setAttachMenuOpen(false);
    try {
      const { path, file_name } = await uploadChatFile(file);
      const sent = await sendMessage(targetId, "", messageType, path, file_name || file.name);
      setMessages((prev) =>
        prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]
      );
    } catch (err) {
      console.error(`Failed to send ${messageType}`, err);
      alert(`Failed to send ${messageType}. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  const triggerImagePicker = () => {
    setAttachMenuOpen(false);
    imageInputRef.current?.click();
  };
  const triggerDocPicker = () => {
    setAttachMenuOpen(false);
    docInputRef.current?.click();
  };

  const startAudioRecording = async () => {
    setAttachMenuOpen(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(audioTimerRef.current);
        setAudioSeconds(0);

        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        await handleFileSelected(file, "audio");
      };

      audioRecorderRef.current = recorder;
      recorder.start();
      setIsRecordingAudio(true);
      setAudioSeconds(0);
      audioTimerRef.current = setInterval(() => setAudioSeconds((s) => s + 1), 1000);
    } catch (err) {
      console.error("Microphone access denied or unavailable", err);
      alert("Couldn't access your microphone. Check browser permissions and try again.");
    }
  };

  const stopAudioRecording = () => {
    audioRecorderRef.current?.stop();
    setIsRecordingAudio(false);
  };

  const cancelAudioRecording = () => {
    if (audioRecorderRef.current) {
      audioRecorderRef.current.onstop = null;
      audioRecorderRef.current.stream?.getTracks().forEach((t) => t.stop());
      audioRecorderRef.current.stop();
    }
    clearInterval(audioTimerRef.current);
    setIsRecordingAudio(false);
    setAudioSeconds(0);
  };
  const startVideoRecording = async () => {
    setAttachMenuOpen(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setVideoPreviewStream(stream);
      setIsRecordingVideo(true);
      setTimeout(() => {
        if (videoPreviewRef.current) videoPreviewRef.current.srcObject = stream;
      }, 0);

      const recorder = new MediaRecorder(stream);
      videoChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) videoChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setVideoPreviewStream(null);
        setIsRecordingVideo(false);

        const blob = new Blob(videoChunksRef.current, { type: "video/webm" });
        const file = new File([blob], `video-${Date.now()}.webm`, { type: "video/webm" });
        await handleFileSelected(file, "video");
      };

      videoRecorderRef.current = recorder;
      recorder.start();
    } catch (err) {
      console.error("Camera access denied or unavailable", err);
      alert("Couldn't access your camera. Check browser permissions and try again.");
    }
  };

  const stopVideoRecording = () => {
    videoRecorderRef.current?.stop();
  };

  const cancelVideoRecording = () => {
    if (videoRecorderRef.current) {
      videoRecorderRef.current.onstop = null;
      videoRecorderRef.current.stream?.getTracks().forEach((t) => t.stop());
      videoRecorderRef.current.stop();
    }
    videoPreviewStream?.getTracks().forEach((t) => t.stop());
    setVideoPreviewStream(null);
    setIsRecordingVideo(false);
  };

  const mergedList = allUsers
    .map((u) => {
      const convo = conversations.find((c) => c.user_id === u.id);
      return convo ? { ...u, ...convo } : { ...u, last_message: null, unread_count: 0 };
    })
    .filter((u) => {
      const s = contactSearch.trim().toLowerCase();
      if (!s) return true;
      return (
        u.full_name?.toLowerCase().includes(s) ||
        u.mobile?.toLowerCase().includes(s)
      );
    });

  const visibleMessages = messageSearch.trim()
    ? messages.filter((m) =>
      m.message?.toLowerCase().includes(messageSearch.trim().toLowerCase())
    )
    : messages;

  const audioMinSec = `${String(Math.floor(audioSeconds / 60)).padStart(2, "0")}:${String(
    audioSeconds % 60
  ).padStart(2, "0")}`;

  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <Header />
        <div className={`chat-page chat-page--${mobileView}`}>
          <div className="chat-user-list">
            <div className="chat-contact-search">
              <span className="chat-search-icon"><FiSearch size={14} /></span>
              <input
                type="text"
                placeholder="Search contacts…"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
              />
            </div>

            {mergedList.map((u) => (
              <div
                key={u.id}
                className={`chat-user-item ${activeUser?.id === u.id ? "active" : ""}`}
                onClick={() => openConversation(u)}
              >
                <div className="chat-user-row">
                  <span className="chat-user-avatar">{getInitials(u.full_name)}</span>
                  <div className="chat-user-info">
                    <span className="chat-user-name">{u.full_name}</span>
                    {u.last_message && (
                      <span className="chat-user-preview">{u.last_message}</span>
                    )}
                  </div>
                </div>
                <div className="chat-user-meta">
                  {u.mobile && (
                    <a
                      href={`tel:${u.mobile}`}
                      className="chat-call-btn"
                      onClick={(e) => e.stopPropagation()}
                      title={`Call ${u.full_name}`}
                    >
                      <FiPhone size={14} />
                    </a>
                  )}
                  {u.unread_count > 0 && (
                    <span className="chat-unread-badge">{u.unread_count}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="chat-window">
            {!activeUser ? (
              <div className="chat-empty">
                <FiMessageSquare size={20} style={{ marginTop: -60, marginBottom: 14, color: "var(--chat-ink-faint)" }} />
                Select a conversation to start chatting
              </div>
            ) : (
              <>
                <div className="chat-window-header">
                  <button
                    type="button"
                    className="chat-menu-btn"
                    title="Back"
                    onClick={backToList}
                  >
                    <FiArrowLeft size={20} />
                  </button>
                  <span className="chat-user-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
                    {getInitials(activeUser.full_name)}
                  </span>
                  <span>{activeUser.full_name}</span>
                  <div className="chat-window-header-actions">
                    <button
                      type="button"
                      className="chat-icon-btn"
                      title="Search in conversation"
                      onClick={() => setMessageSearchOpen((v) => !v)}
                    >
                      <FiSearch size={16} />
                    </button>
                    {activeUser.mobile && (
                      <a href={`tel:${activeUser.mobile}`} className="chat-call-btn">
                        <FiPhone size={13} /> <span className="chat-call-label">Call</span>
                      </a>
                    )}
                  </div>
                </div>

                {messageSearchOpen && (
                  <div className="chat-message-search">
                    <input
                      type="text"
                      autoFocus
                      placeholder="Search messages in this chat…"
                      value={messageSearch}
                      onChange={(e) => setMessageSearch(e.target.value)}
                    />
                    {messageSearch && (
                      <span className="chat-message-search-count">
                        {visibleMessages.length} match{visibleMessages.length !== 1 ? "es" : ""}
                      </span>
                    )}
                    <button
                      type="button"
                      className="chat-icon-btn"
                      onClick={() => { setMessageSearchOpen(false); setMessageSearch(""); }}
                    >
                      <FiX size={16} />
                    </button>
                  </div>
                )}

                <div className="chat-messages">
                  {visibleMessages.map((m, i) => {
                    const mine = Number(m.sender_id) === myId;
                    const highlight =
                      messageSearch.trim() &&
                      m.message?.toLowerCase().includes(messageSearch.trim().toLowerCase());

                    const prevMsg = visibleMessages[i - 1];
                    const showDateDivider =
                      !prevMsg || formatDayLabel(prevMsg.created_at) !== formatDayLabel(m.created_at);

                    return (
                      <div key={m.id}>
                        {showDateDivider && (
                          <div className="chat-date-divider">
                            <span>{formatDayLabel(m.created_at)}</span>
                          </div>
                        )}

                        <div className={`chat-message-row ${mine ? "sent" : "received"}`}>
                          <div
                            className={`chat-bubble ${mine ? "sent" : "received"} ${highlight ? "chat-bubble--highlight" : ""
                              }`}
                          >
                            {m.message_type === "image" && m.file_url && (
                              <img
                                src={resolveFileUrl(m.file_url)}
                                alt={m.file_name || "Image"}
                                className="chat-media-image"
                                onClick={() => setLightboxImage(resolveFileUrl(m.file_url))}
                              />
                            )}

                            {m.message_type === "video" && m.file_url && (
                              <video src={resolveFileUrl(m.file_url)} controls className="chat-media-video" />
                            )}

                            {m.message_type === "audio" && m.file_url && (
                              <audio src={resolveFileUrl(m.file_url)} controls className="chat-media-audio" />
                            )}

                            {m.message_type === "document" && m.file_url && (
                              <a
                                href={resolveFileUrl(m.file_url)}
                                target="_blank"
                                rel="noreferrer"
                                className="chat-media-doc"
                              >
                                <FiFile size={15} /> {m.file_name || "Document"}
                              </a>
                            )}

                            {m.message && <div className="chat-bubble-text">{m.message}</div>}

                            <div className="chat-bubble-meta">
                              <span className="chat-bubble-time">{formatTime(m.created_at)}</span>
                              {mine && <MessageTicks read={m.is_read} />}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                {isRecordingVideo && (
                  <div className="chat-video-recording-bar">
                    <video ref={videoPreviewRef} autoPlay muted className="chat-video-preview" />
                    <div className="chat-video-recording-actions">
                      <span className="chat-recording-dot" /> Recording…
                      <button type="button" className="chat-icon-btn" onClick={cancelVideoRecording}>
                        Cancel
                      </button>
                      <button type="button" className="chat-send-recording-btn" onClick={stopVideoRecording}>
                        Send
                      </button>
                    </div>
                  </div>
                )}

                {isRecordingAudio ? (
                  <div className="chat-input-row chat-input-row--recording">
                    <span className="chat-recording-dot" />
                    <span className="chat-recording-time">{audioMinSec}</span>
                    <span className="chat-recording-label">Recording voice message…</span>
                    <button type="button" className="chat-icon-btn" onClick={cancelAudioRecording}>
                      <FiX size={16} />
                    </button>
                    <button type="button" className="chat-send-recording-btn" onClick={stopAudioRecording}>
                      Send
                    </button>
                  </div>
                ) : (
                  <div className="chat-input-row">
                    <div className="chat-attach-wrap">
                      <button
                        type="button"
                        className="chat-icon-btn"
                        title="Attach"
                        onClick={() => setAttachMenuOpen((v) => !v)}
                        disabled={uploading}
                      >
                        <FiPaperclip size={18} />
                      </button>
                      {attachMenuOpen && (
                        <div className="chat-attach-menu">
                          <button type="button" onClick={triggerImagePicker}>
                            <FiImage size={15} /> Photo
                          </button>
                          <button type="button" onClick={triggerDocPicker}>
                            <FiFile size={15} /> Document
                          </button>
                          <button type="button" onClick={startVideoRecording}>
                            <FiVideo size={15} /> Record video
                          </button>
                        </div>
                      )}
                    </div>

                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleFileSelected(file, "image");
                        e.target.value = "";
                      }}
                    />
                    <input
                      ref={docInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleFileSelected(file, "document");
                        e.target.value = "";
                      }}
                    />

                    <input
                      type="text"
                      placeholder={uploading ? "Sending…" : "Type a message…"}
                      value={draft}
                      disabled={uploading}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    />

                    {draft.trim() ? (
                      <button onClick={handleSend} disabled={uploading}>
                        <FiSend size={14} /> Send
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="chat-icon-btn chat-mic-btn"
                        title="Record voice message"
                        onClick={startAudioRecording}
                        disabled={uploading}
                      >
                        <FiMic size={18} />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {lightboxImage && (
        <div className="chat-lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <button
            type="button"
            className="chat-lightbox-close"
            onClick={() => setLightboxImage(null)}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="chat-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}